import React, { useCallback, useEffect, useState } from 'react';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';
import {
  ordersAdminService,
  type PaymentOrder,
  type PaymentOrderDetail,
  type OrderStatus,
  type SourceType,
  type ListOrdersQuery,
  type FulfillmentStatus,
  type ExportOptions,
} from '../../services/admin-orders.service.js';
import { getAdminJwt, getAdminToken } from '../../utils/pricingContext.js';
import './admin-shared.css';
import './OrdersAdminPage.css';

const ORDER_STATUSES: OrderStatus[] = [
  'PENDING',
  'REQUIRES_ACTION',
  'SUCCEEDED',
  'FAILED',
  'CANCELLED',
  'REFUNDED',
  'PARTIALLY_REFUNDED',
];

const SOURCE_TYPES: SourceType[] = ['BOOKING', 'TOURNAMENT_ENTRY', 'INVOICE', 'SUBSCRIPTION', 'UNKNOWN'];

const FULFILLMENT_STATUSES: FulfillmentStatus[] = ['PENDING', 'PROCESSING', 'SHIPPED', 'DELIVERED', 'RETURNED'];

const CARRIERS = ['DHL', 'FedEx', 'UPS', 'The Courier Guy', 'Aramex', 'PostNet', 'Other'];

const formatAmount = (cents: number, currency: string): string => {
  const value = cents / 100;
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(value);
};

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusBadgeClass = (status: OrderStatus): string => {
  switch (status) {
    case 'SUCCEEDED':
      return 'badge-success';
    case 'PENDING':
    case 'REQUIRES_ACTION':
      return 'badge-warning';
    case 'FAILED':
    case 'CANCELLED':
      return 'badge-error';
    case 'REFUNDED':
    case 'PARTIALLY_REFUNDED':
      return 'badge-info';
    default:
      return '';
  }
};

const OrdersAdminPage: React.FC = () => {
  const [orders, setOrders] = useState<PaymentOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<PaymentOrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Modal states
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');

  const [showFulfillmentModal, setShowFulfillmentModal] = useState(false);
  const [fulfillmentStatus, setFulfillmentStatus] = useState<FulfillmentStatus>('PENDING');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [carrier, setCarrier] = useState('');
  const [fulfillmentNotes, setFulfillmentNotes] = useState('');

  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'json' | 'xlsx'>('csv');
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');
  const [exportIncludeTransactions, setExportIncludeTransactions] = useState(false);

  const storeToken = useGlobalStore((s: GlobalState) => s.token);

  const [filters, setFilters] = useState<ListOrdersQuery>({
    status: undefined,
    sourceType: undefined,
    sourceId: '',
    skip: 0,
    take: 20,
  });

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await ordersAdminService.listOrders(filters);
      setOrders(response.data);
      setTotal(response.total);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const hasAuth = storeToken?.trim() || getAdminJwt()?.trim() || getAdminToken()?.trim();
    if (hasAuth) {
      loadOrders();
    }
  }, [loadOrders, storeToken]);

  const handleFilterChange = (key: keyof ListOrdersQuery, value: string | number | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      skip: key !== 'skip' ? 0 : prev.skip, // Reset pagination on filter change
    }));
  };

  const handleViewOrder = async (orderId: string) => {
    setDetailLoading(true);
    setSelectedOrder(null);
    try {
      const detail = await ordersAdminService.getOrder(orderId);
      setSelectedOrder(detail);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to load order details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    if (!confirm('Are you sure you want to cancel this order?')) return;
    try {
      await ordersAdminService.cancelOrder(orderId);
      await loadOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(null);
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to cancel order');
    }
  };

  const handleCloseDetail = () => {
    setSelectedOrder(null);
  };

  const clearMessages = () => {
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  const handleCapturePayment = async (orderId: string) => {
    if (!confirm('Capture payment for this order?')) return;
    clearMessages();
    setActionLoading(true);
    try {
      await ordersAdminService.capturePayment(orderId);
      setSuccessMessage('Payment captured successfully');
      await loadOrders();
      if (selectedOrder?.id === orderId) {
        const updated = await ordersAdminService.getOrder(orderId);
        setSelectedOrder(updated);
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to capture payment');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenRefundModal = () => {
    if (!selectedOrder) return;
    setRefundAmount((selectedOrder.amount / 100).toFixed(2));
    setRefundReason('');
    setShowRefundModal(true);
  };

  const handleRefund = async () => {
    if (!selectedOrder) return;
    clearMessages();
    setActionLoading(true);
    try {
      const amount = refundAmount ? Math.round(parseFloat(refundAmount) * 100) : undefined;
      await ordersAdminService.refundOrder(selectedOrder.id, {
        amount,
        reason: refundReason || undefined,
      });
      setSuccessMessage('Refund processed successfully');
      setShowRefundModal(false);
      await loadOrders();
      const updated = await ordersAdminService.getOrder(selectedOrder.id);
      setSelectedOrder(updated);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to process refund');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenFulfillmentModal = () => {
    setFulfillmentStatus('PROCESSING');
    setTrackingNumber('');
    setTrackingUrl('');
    setCarrier('');
    setFulfillmentNotes('');
    setShowFulfillmentModal(true);
  };

  const handleUpdateFulfillment = async () => {
    if (!selectedOrder) return;
    clearMessages();
    setActionLoading(true);
    try {
      await ordersAdminService.updateFulfillment(selectedOrder.id, {
        status: fulfillmentStatus,
        trackingNumber: trackingNumber || undefined,
        trackingUrl: trackingUrl || undefined,
        carrier: carrier || undefined,
        notes: fulfillmentNotes || undefined,
      });
      setSuccessMessage(`Order marked as ${fulfillmentStatus}`);
      setShowFulfillmentModal(false);
      await loadOrders();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to update fulfillment');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExport = async () => {
    clearMessages();
    setActionLoading(true);
    try {
      const options: ExportOptions = {
        format: exportFormat,
        dateFrom: exportDateFrom || undefined,
        dateTo: exportDateTo || undefined,
        status: filters.status,
        includeTransactions: exportIncludeTransactions,
      };
      const blob = await ordersAdminService.exportOrders(options);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders-export-${new Date().toISOString().slice(0, 10)}.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSuccessMessage('Export downloaded successfully');
      setShowExportModal(false);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to export orders');
    } finally {
      setActionLoading(false);
    }
  };

  const currentPage = Math.floor((filters.skip ?? 0) / (filters.take ?? 20)) + 1;
  const totalPages = Math.ceil(total / (filters.take ?? 20));

  return (
    <div className="orders-admin-page">
      <section className="page-hero orders-admin-hero">
        <div className="container">
          <span className="badge badge-lime">Admin</span>
          <h1>Orders & Fulfillment</h1>
          <p>View payment orders, track status, and manage fulfillment.</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="admin-card">
            <h2>Filters</h2>
            <div className="orders-filters">
              <select
                value={filters.status ?? ''}
                onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
              >
                <option value="">All Statuses</option>
                {ORDER_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <select
                value={filters.sourceType ?? ''}
                onChange={(e) => handleFilterChange('sourceType', e.target.value || undefined)}
              >
                <option value="">All Sources</option>
                {SOURCE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={filters.sourceId ?? ''}
                onChange={(e) => handleFilterChange('sourceId', e.target.value || undefined)}
                placeholder="Source ID (e.g., YOLO-123)"
              />
              <button type="button" className="btn btn-primary" onClick={loadOrders}>
                Search
              </button>
            </div>
            {loading && <p className="status-message">Loading orders...</p>}
            {errorMessage && <p className="status-message status-error">{errorMessage}</p>}
            {successMessage && <p className="status-message status-success">{successMessage}</p>}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="orders-layout">
            <div className="orders-list-panel">
              <div className="orders-header">
                <div className="orders-title-row">
                  <h2>Orders ({total})</h2>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => setShowExportModal(true)}
                  >
                    Export
                  </button>
                </div>
                <div className="orders-pagination">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={currentPage <= 1}
                    onClick={() => handleFilterChange('skip', ((currentPage - 2) * (filters.take ?? 20)))}
                  >
                    Prev
                  </button>
                  <span>
                    Page {currentPage} of {totalPages || 1}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={currentPage >= totalPages}
                    onClick={() => handleFilterChange('skip', (currentPage * (filters.take ?? 20)))}
                  >
                    Next
                  </button>
                </div>
              </div>

              <div className="orders-table">
                <div className="orders-table-header">
                  <span>Order</span>
                  <span>Amount</span>
                  <span>Status</span>
                  <span>Source</span>
                  <span>Created</span>
                  <span>Actions</span>
                </div>
                {orders.length === 0 && !loading && (
                  <div className="orders-empty">No orders found</div>
                )}
                {orders.map((order) => (
                  <div
                    key={order.id}
                    className={`orders-table-row ${selectedOrder?.id === order.id ? 'selected' : ''}`}
                  >
                    <span className="order-id" title={order.id}>
                      {order.sourceId || order.id.slice(0, 8)}
                    </span>
                    <span className="order-amount">{formatAmount(order.amount, order.currency)}</span>
                    <span>
                      <span className={`badge ${getStatusBadgeClass(order.status)}`}>{order.status}</span>
                    </span>
                    <span className="order-source">{order.sourceType}</span>
                    <span className="order-date">{formatDate(order.createdAt)}</span>
                    <span className="order-actions">
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => handleViewOrder(order.id)}
                      >
                        View
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {(selectedOrder || detailLoading) && (
              <div className="order-detail-panel">
                <div className="order-detail-header">
                  <h3>Order Details</h3>
                  <button type="button" className="btn btn-ghost" onClick={handleCloseDetail}>
                    Close
                  </button>
                </div>
                {detailLoading && <p className="status-message">Loading details...</p>}
                {selectedOrder && (
                  <div className="order-detail-content">
                    <div className="detail-section">
                      <h4>Overview</h4>
                      <dl className="detail-grid">
                        <dt>Order ID</dt>
                        <dd>{selectedOrder.id}</dd>
                        <dt>Source ID</dt>
                        <dd>{selectedOrder.sourceId || 'N/A'}</dd>
                        <dt>Amount</dt>
                        <dd>{formatAmount(selectedOrder.amount, selectedOrder.currency)}</dd>
                        {selectedOrder.feeAmount && (
                          <>
                            <dt>Fee</dt>
                            <dd>{formatAmount(selectedOrder.feeAmount, selectedOrder.currency)}</dd>
                          </>
                        )}
                        <dt>Status</dt>
                        <dd>
                          <span className={`badge ${getStatusBadgeClass(selectedOrder.status)}`}>
                            {selectedOrder.status}
                          </span>
                        </dd>
                        <dt>Source Type</dt>
                        <dd>{selectedOrder.sourceType}</dd>
                        <dt>Created</dt>
                        <dd>{formatDate(selectedOrder.createdAt)}</dd>
                        <dt>Updated</dt>
                        <dd>{formatDate(selectedOrder.updatedAt)}</dd>
                        {selectedOrder.description && (
                          <>
                            <dt>Description</dt>
                            <dd>{selectedOrder.description}</dd>
                          </>
                        )}
                      </dl>
                    </div>

                    {selectedOrder.intents.length > 0 && (
                      <div className="detail-section">
                        <h4>Payment Intents</h4>
                        <div className="detail-list">
                          {selectedOrder.intents.map((intent) => (
                            <div key={intent.id} className="detail-list-item">
                              <div>
                                <strong>{intent.provider}</strong>
                                <span className={`badge ${getStatusBadgeClass(intent.status)}`}>
                                  {intent.status}
                                </span>
                              </div>
                              <div className="muted">
                                {formatAmount(intent.amount, intent.currency)} &bull;{' '}
                                {formatDate(intent.createdAt)}
                              </div>
                              {intent.externalRef && (
                                <div className="muted">Ref: {intent.externalRef}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedOrder.transactions.length > 0 && (
                      <div className="detail-section">
                        <h4>Transactions</h4>
                        <div className="detail-list">
                          {selectedOrder.transactions.map((tx) => (
                            <div key={tx.id} className="detail-list-item">
                              <div>
                                <strong>{tx.type}</strong>
                                <span className={`badge ${getStatusBadgeClass(tx.status)}`}>
                                  {tx.status}
                                </span>
                              </div>
                              <div className="muted">
                                {formatAmount(tx.amount, tx.currency)} &bull; {tx.provider} &bull;{' '}
                                {formatDate(tx.occurredAt)}
                              </div>
                              {tx.externalRef && <div className="muted">Ref: {tx.externalRef}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="detail-actions">
                      {selectedOrder.status === 'PENDING' && (
                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={() => handleCancelOrder(selectedOrder.id)}
                          disabled={actionLoading}
                        >
                          Cancel Order
                        </button>
                      )}
                      {selectedOrder.status === 'REQUIRES_ACTION' && (
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => handleCapturePayment(selectedOrder.id)}
                          disabled={actionLoading}
                        >
                          Capture Payment
                        </button>
                      )}
                      {selectedOrder.status === 'SUCCEEDED' && (
                        <>
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={handleOpenRefundModal}
                            disabled={actionLoading}
                          >
                            Refund
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleOpenFulfillmentModal}
                            disabled={actionLoading}
                          >
                            Update Fulfillment
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Refund Modal */}
      {showRefundModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowRefundModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Process Refund</h3>
              <button type="button" className="btn btn-ghost" onClick={() => setShowRefundModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>
                Order: <strong>{selectedOrder.sourceId || selectedOrder.id.slice(0, 8)}</strong>
              </p>
              <p>
                Original Amount: <strong>{formatAmount(selectedOrder.amount, selectedOrder.currency)}</strong>
              </p>
              <div className="form-group">
                <label htmlFor="refund-amount">Refund Amount ({selectedOrder.currency})</label>
                <input
                  id="refund-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  max={selectedOrder.amount / 100}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  placeholder="Leave empty for full refund"
                />
              </div>
              <div className="form-group">
                <label htmlFor="refund-reason">Reason (optional)</label>
                <textarea
                  id="refund-reason"
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Customer requested refund..."
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={() => setShowRefundModal(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleRefund}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : 'Process Refund'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fulfillment Modal */}
      {showFulfillmentModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowFulfillmentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Update Fulfillment</h3>
              <button type="button" className="btn btn-ghost" onClick={() => setShowFulfillmentModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="fulfillment-status">Status</label>
                <select
                  id="fulfillment-status"
                  value={fulfillmentStatus}
                  onChange={(e) => setFulfillmentStatus(e.target.value as FulfillmentStatus)}
                >
                  {FULFILLMENT_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="carrier">Carrier</label>
                <select
                  id="carrier"
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                >
                  <option value="">Select carrier...</option>
                  {CARRIERS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="tracking-number">Tracking Number</label>
                <input
                  id="tracking-number"
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder="e.g., 1Z999AA10123456784"
                />
              </div>
              <div className="form-group">
                <label htmlFor="tracking-url">Tracking URL (optional)</label>
                <input
                  id="tracking-url"
                  type="url"
                  value={trackingUrl}
                  onChange={(e) => setTrackingUrl(e.target.value)}
                  placeholder="https://..."
                />
              </div>
              <div className="form-group">
                <label htmlFor="fulfillment-notes">Notes (optional)</label>
                <textarea
                  id="fulfillment-notes"
                  value={fulfillmentNotes}
                  onChange={(e) => setFulfillmentNotes(e.target.value)}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={() => setShowFulfillmentModal(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleUpdateFulfillment}
                disabled={actionLoading}
              >
                {actionLoading ? 'Updating...' : 'Update Fulfillment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Export Orders</h3>
              <button type="button" className="btn btn-ghost" onClick={() => setShowExportModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="export-format">Format</label>
                <select
                  id="export-format"
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value as 'csv' | 'json' | 'xlsx')}
                >
                  <option value="csv">CSV</option>
                  <option value="json">JSON</option>
                  <option value="xlsx">Excel (XLSX)</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="export-date-from">From Date</label>
                <input
                  id="export-date-from"
                  type="date"
                  value={exportDateFrom}
                  onChange={(e) => setExportDateFrom(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="export-date-to">To Date</label>
                <input
                  id="export-date-to"
                  type="date"
                  value={exportDateTo}
                  onChange={(e) => setExportDateTo(e.target.value)}
                />
              </div>
              <div className="form-group form-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={exportIncludeTransactions}
                    onChange={(e) => setExportIncludeTransactions(e.target.checked)}
                  />
                  Include transaction details
                </label>
              </div>
              <p className="muted">
                {filters.status ? `Exporting ${filters.status} orders only` : 'Exporting all statuses'}
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={() => setShowExportModal(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleExport}
                disabled={actionLoading}
              >
                {actionLoading ? 'Exporting...' : 'Download Export'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersAdminPage;
